import { useState, useEffect } from 'react';
import {
    useLocation,
    useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import { Card } from "@mui/material";

import { useCompanyHook } from '../../Hooks/Companies/companyHooks';
import { useCompanyUsersHook } from '../../Hooks/Companies/companyUsersHooks';

import { Company } from '../../Models/Company/Company';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { CompanyField, CompanyUserField } from '../../Utilities/Enums/ParametersEnum';
import { TriggerTypeEnum } from '../../Utilities/Enums/CompanyEnum';

import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CompanyDetailsForm from './CompanyDetailsForm';
import CompanyLicenses from './CompanyLicenses';
import CompanyUsers from './CompanyUsers';

const DetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
    overflow: visible !important;
`;

export default function CompanyDetails() {
    // Variables
    let navigate = useNavigate();
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let companyid = queryParams.get('id') ?? "0";
    let view = parseInt(queryParams.get('view') ?? ViewType.PREVIEW.toString());

    // Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: companyid,
    });
    let [viewType, setviewType] = useState(view ?? ViewType.ADD);
    let [openDelete, setopenDelete] = useState(false);
    let [deleteType, setdeleteType] = useState('');
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);
    let [formData, setformData] = useState<Company>(new Company());
    let [previewData, setpreviewData] = useState<Company>(new Company());
    let [errorMessages, setErrorMessages]: any = useState({});
    let [reinitializeData, setreinitializeData] = useState(false);

    const q_queryKey = [CachingEnum.COMPANIES, queryStr]
    const { DropdownData, getCompanyById, deleteCompanyById } = useCompanyHook(q_queryKey, queryStr, false);
    const { deleteCompanyUserById, syncCompanyUser } = useCompanyUsersHook(q_queryKey, queryStr, false);
    let detailsCommonProps: any = {
        companyid: companyid,
        viewType: viewType,
        setviewType: setviewType,
        setopenDelete: setopenDelete,
        setdeleteType: setdeleteType,
        settoDeleteItem: settoDeleteItem,
        previewData: previewData,
        formData: formData,
        setformData: setformData,
        errorMessages: errorMessages,
        setErrorMessages: setErrorMessages,
        DropdownData: DropdownData,
        reinitializeData: reinitializeData,
        setreinitializeData: setreinitializeData,
    };

    //Event Handlers
    const getCompanyData = async () => {
        if(companyid != '0') {
            let syncResponse = await syncCompanyUser(JSON.stringify({triggerType: TriggerTypeEnum.PAGE_LOAD}));
            let getDetails = await getCompanyById(companyid);
            let _formData = {...formData};

            if(getDetails?.data) {
                _formData = {
                    ...formData,
                    ...getDetails.data,
                    [CompanyField.VENDOR_PUBLIC_ID]: getDetails?.data?.[CompanyField.VENDOR]?.publicId ?? null,
                };
            }

            setformData(_formData);
            setpreviewData(_formData);
        }

        if(reinitializeData) {
            setreinitializeData(false);
        }
    }

    detailsCommonProps = {...detailsCommonProps, getCompanyData: getCompanyData,};

    const deleteCompany = async (remarks: any) => {
        if(toDeleteItem?.[CompanyField.PUBLIC_ID]) {
            let deleteReponse: any = await deleteCompanyById(toDeleteItem?.[CompanyField.PUBLIC_ID], remarks);

            if(deleteReponse?.isSuccess) {
                handleCloseDeleteConfirmation();
                navigate(RouteEnum.COMPANIES, { replace: true });
            }
        }
    }

    const deleteCompanyUser = async (remarks: any) => {
        if(toDeleteItem?.[CompanyUserField.PUBLIC_ID]) {
            let deleteReponse: any = await deleteCompanyUserById(toDeleteItem?.[CompanyUserField.PUBLIC_ID]);

            if(deleteReponse?.isSuccess) {
                handleCloseDeleteConfirmation();
                getCompanyData();
            }
        }
    }

    const handleCloseDeleteConfirmation = () => {
        setopenDelete(false);
        setdeleteType('');
        settoDeleteItem(null);
    }

    useEffect(() => {
            getCompanyData();
    }, [companyid]);
    
    //Rendering Functions
    const renderCompanyDetails = () => {
        return <CompanyDetailsForm {...detailsCommonProps} />
    }

    const renderCompanyLicenses = () => {
        return <CompanyLicenses {...detailsCommonProps} />
    }

    const renderCompanyUsers = () => {
        return <CompanyUsers {...detailsCommonProps} />
    }

    return <DetailsContainer>
        {renderCompanyDetails()}
        {viewType != ViewType.ADD && !reinitializeData && renderCompanyLicenses()}
        {viewType != ViewType.ADD && !reinitializeData && renderCompanyUsers()}

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            handleClose={handleCloseDeleteConfirmation}
            ItemToDelete={(deleteType == "company" ? (toDeleteItem?.comName ?? null) : (toDeleteItem?.userName ?? null))} // Optional to display name of the item to be deleted
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(deleteType == "company") {
                    deleteCompany(remarks);
                } else {
                    deleteCompanyUser(remarks);
                }
            }} />
        }
    </DetailsContainer>
}
